import { useEffect, useState, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AtmosphereLayer } from './AtmosphereLayer';
import { BuildUpSequence } from './BuildUpSequence';
import { CinematicAudio } from './CinematicAudio';
import { CinematicPhotoSequence } from './CinematicPhotoSequence';
import { getCinematicPalette } from './themePalettes';
import type { AdaptiveRevealConfig } from './adaptive/adaptiveConfig';

const EASE = [0.22, 0.61, 0.36, 1] as const;

export type ShellPhase = 'atmosphere' | 'buildup' | 'photos' | 'scene' | 'done';

interface CinematicShellProps {
  /** animationTheme value used to pick palette */
  themeValue?: string;
  /** Skip the cinematic intro entirely (e.g. owner preview) */
  skipIntro?: boolean;
  /** Called once the buildup ends and the scene should start playing */
  onIntroComplete: () => void;
  /** Render-prop receives current shell phase so the scene can mount on time */
  children: (phase: ShellPhase) => React.ReactNode;
  /** True once the actual scene reaches its done state — fades out atmosphere */
  sceneDone: boolean;
  /** Optional ambient audio src (e.g. '/audio/cinematic-bed.mp3') */
  audioSrc?: string;
  /** Uploaded photos shown full-screen after intro words and before the message scene */
  photoUrls?: string[];
  /** Adaptive reveal config — when present, drives backdrop layers, pacing, audio bed */
  config?: AdaptiveRevealConfig;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * CinematicShell — wraps any reveal scene in a premium pre-reveal atmosphere.
 *
 * Phase progression:
 *   1. atmosphere  — dark cinematic environment fades in (~1.4s)
 *   2. buildup     — three-beat typography build-up (~6.5s, skippable)
 *   3. scene       — child scene mounts and plays its own animation
 *   4. done        — atmosphere fades, scene stays mounted (handled by parent)
 *
 * Honors prefers-reduced-motion by skipping atmosphere + buildup.
 */
export function CinematicShell({
  themeValue,
  skipIntro = false,
  onIntroComplete,
  children,
  sceneDone,
  audioSrc,
  photoUrls,
  config,
}: CinematicShellProps) {
  const palette = useMemo(
    () => config?.palette ?? getCinematicPalette(themeValue),
    [config, themeValue],
  );
  const reduced = useMemo(prefersReducedMotion, []);

  const [phase, setPhase] = useState<ShellPhase>(() => {
    if (skipIntro || reduced) return 'scene';
    return 'atmosphere';
  });

  // Atmosphere → buildup after a short breath
  useEffect(() => {
    if (phase !== 'atmosphere') return;
    const t = setTimeout(() => setPhase('buildup'), 1500);
    return () => clearTimeout(t);
  }, [phase]);

  // Skip-intro / reduced-motion: tell parent immediately
  useEffect(() => {
    if (phase === 'scene') {
      onIntroComplete();
    }
  }, [phase, onIntroComplete]);

  const hasPhotos = (photoUrls?.filter(Boolean).length ?? 0) > 0;

  const handleBuildupDone = useCallback(() => {
    setPhase(hasPhotos ? 'photos' : 'scene');
  }, [hasPhotos]);

  const handleSkipIntro = useCallback(() => {
    setPhase('scene');
  }, []);

  // Mark fully done so atmosphere fades out
  useEffect(() => {
    if (sceneDone && phase !== 'done') setPhase('done');
  }, [sceneDone, phase]);

  const showAtmosphere = phase === 'atmosphere' || phase === 'buildup';

  // Subtle breathing pulse on the atmosphere overlay (3% opacity oscillation).
  // Disabled if reduced motion is requested or the chosen theme is 'still'.
  const motionAccent = config?.emotionalTheme.motion ?? 'breath';
  const allowBreath = !reduced && motionAccent !== 'still';

  return (
    <div className="relative">
      {/* Atmosphere overlay — covers viewport during intro, fades over scene at end */}
      <AnimatePresence>
        {showAtmosphere && (
          <motion.div
            key="cinematic-overlay"
            className="fixed inset-0 z-[60]"
            initial={{ opacity: 0 }}
            animate={
              allowBreath
                ? { opacity: [1, 0.97, 1] }
                : { opacity: 1 }
            }
            exit={{ opacity: 0, transition: { duration: 0.8, ease: EASE } }}
            transition={
              allowBreath
                ? { opacity: { duration: 4.2, repeat: Infinity, ease: 'easeInOut' } }
                : { duration: 1.0, ease: EASE }
            }
          >
            <AtmosphereLayer palette={palette} config={config} />
            {phase === 'buildup' && (
              <BuildUpSequence
                palette={palette}
                onComplete={handleBuildupDone}
                beats={config?.introSequence?.beats}
                heldBreathMs={reduced ? 0 : (config?.heldBreathMs ?? 0)}
              />
            )}

            {/* Skip intro */}
            <motion.button
              type="button"
              onClick={handleSkipIntro}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8, duration: 0.6 }}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-white/90 transition-colors px-4 py-2 rounded-full backdrop-blur-sm bg-black/20"
            >
              Skip intro →
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {phase === 'photos' && (
          <CinematicPhotoSequence
            photoUrls={photoUrls}
            palette={palette}
            onComplete={() => setPhase('scene')}
          />
        )}
      </AnimatePresence>

      {/* Scene mounts only after intro completes so its timers don't run under the overlay */}
      {(phase === 'scene' || phase === 'done') && children(phase)}

      <CinematicAudio src={audioSrc} active={showAtmosphere || phase === 'photos' || phase === 'scene'} />
    </div>
  );
}
